import { Upload01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import { useCaseDetail } from "@/hooks/use-case-detail";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { statusBadgeVariant } from "@/lib/case-format";
import { formatReadyGateIssueLine } from "@/lib/ready-gate-copy";

export function VersionAssetsPanel({
  caseId,
  versionId,
}: {
  caseId: string;
  versionId: string;
}) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const detailQuery = useCaseDetail(caseId);
  const version = detailQuery.data?.versions.find((v) => v.id === versionId);
  const versionStatus = version?.status ?? null;
  const canLoadPackage =
    Boolean(version?.phoneJsonR2Key) ||
    Boolean(version?.hasPipeline) ||
    versionStatus === "published" ||
    versionStatus === "failed";
  const [replaceBusyId, setReplaceBusyId] = useState<string | null>(null);
  const [assetError, setAssetError] = useState<string | null>(null);

  const assetsQuery = useQuery({
    queryKey: ["assets", versionId],
    queryFn: () => api.listAssets(versionId),
    enabled: auth.status === "authed" && canLoadPackage,
    refetchInterval: 8000,
  });

  const mediaReadyQuery = useQuery({
    queryKey: ["media-readiness", versionId],
    queryFn: () => api.getMediaReadiness(versionId),
    enabled: auth.status === "authed" && canLoadPackage,
    refetchInterval: 5000,
  });

  const assets = assetsQuery.data?.assets ?? [];
  const mediaReady = mediaReadyQuery.data ?? null;
  const showAssets = assets.length > 0 || canLoadPackage;

  const onReplaceAsset = async (assetId: string, file: File | null) => {
    if (!file || replaceBusyId) return;
    const asset = assets.find((a) => a.id === assetId);
    const tag = asset?.placeholderTag ?? assetId;
    setReplaceBusyId(assetId);
    setAssetError(null);
    try {
      await api.replaceAsset(versionId, assetId, file);
      toast.add({
        type: "success",
        title: "素材已替换",
        description: `${tag} 已用 ${file.name} 更新。`,
      });
      await queryClient.invalidateQueries({ queryKey: ["assets", versionId] });
      await queryClient.invalidateQueries({
        queryKey: ["phone-json", versionId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["media-readiness", versionId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["case-preview", caseId],
      });
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "replace_failed";
      const note =
        err instanceof ApiError && err.note ? `${code}: ${err.note}` : code;
      setAssetError(note);
      toast.add({
        type: "error",
        title: "替换失败",
        description: note,
        priority: "high",
      });
    } finally {
      setReplaceBusyId(null);
    }
  };

  if (!showAssets) {
    return (
      <Empty className="border border-border py-8">
        <EmptyHeader>
          <EmptyTitle>尚无素材</EmptyTitle>
          <EmptyDescription>
            流水线素材步骤完成后才会出现素材。
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>素材</CardTitle>
        <CardDescription>
          R2 中的生成媒体。替换任意文件可覆盖模型输出。公开 URL 位于{" "}
          <code className="text-xs">/api/media/assets/</code>。
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {assetsQuery.isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex gap-4">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="size-12" />
                <Skeleton className="h-8 w-20" />
              </div>
            ))}
          </div>
        ) : assets.length === 0 ? (
          <Empty className="border border-border py-8">
            <EmptyHeader>
              <EmptyTitle>尚无素材</EmptyTitle>
              <EmptyDescription>
                asset_plan 完成后才会出现素材。替换任意文件可覆盖模型输出。
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="max-h-96 overflow-auto rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>标签</TableHead>
                  <TableHead>类型</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>预览</TableHead>
                  <TableHead>替换</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assets.map((asset) => (
                  <TableRow key={asset.id}>
                    <TableCell className="font-mono text-xs">
                      {asset.placeholderTag}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{asset.type}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant(asset.status)}>
                        {asset.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {asset.publicUrl && asset.type === "image" ? (
                        <a
                          href={asset.publicUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-block"
                        >
                          <img
                            src={`${asset.publicUrl}?v=${encodeURIComponent(asset.updatedAt)}`}
                            alt={asset.placeholderTag}
                            className="size-12 rounded-md border border-border object-cover"
                          />
                        </a>
                      ) : asset.publicUrl && asset.type === "audio" ? (
                        <audio
                          controls
                          preload="none"
                          src={`${asset.publicUrl}?v=${encodeURIComponent(asset.updatedAt)}`}
                          className="h-8 max-w-[12rem]"
                        />
                      ) : asset.publicUrl ? (
                        <a
                          href={asset.publicUrl}
                          className="text-xs text-muted-foreground underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          打开
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <label className="inline-flex cursor-pointer items-center gap-2">
                        <input
                          type="file"
                          className="sr-only"
                          disabled={replaceBusyId === asset.id}
                          onChange={(e) => {
                            const f = e.target.files?.[0] ?? null;
                            void onReplaceAsset(asset.id, f);
                            e.target.value = "";
                          }}
                        />
                        <Button
                          variant="outline"
                          disabled={replaceBusyId === asset.id}
                          render={<span />}
                          nativeButton={false}
                        >
                          {replaceBusyId === asset.id ? (
                            <Spinner />
                          ) : (
                            <HugeiconsIcon
                              icon={Upload01Icon}
                              data-icon="inline-start"
                            />
                          )}
                          上传
                        </Button>
                      </label>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {mediaReady && !mediaReady.ok && mediaReady.issues.length > 0 ? (
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium">媒体就绪问题</p>
            <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto text-xs text-muted-foreground">
              {mediaReady.issues.map((issue) => (
                <li key={`${issue.path}:${issue.reason}`}>
                  {formatReadyGateIssueLine(issue)}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {assetError ? (
          <Alert variant="destructive">
            <AlertTitle>素材错误</AlertTitle>
            <AlertDescription>{assetError}</AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}
